import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import {
  computeAddOnFlatAmount,
  formatQuantity,
} from '~components/Project/QuotePdf/utils';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { Project, ProjectMaterialTotal } from '~hooks/useProjects/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import {
  getRateTypeLabel,
  QUANTITY_AND_TIME_RATE_TYPES,
  QuantityOrTimeRateType,
} from '~pages/Settings/RatesManagement/rateUtils';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';
import { formatPercentage } from '~utils/utilFunctions';

// The quote's inputs are PROJECTIONS of the saved models, not independent
// declarations: every field's type is read off the model that owns it, so a
// model field that changes type or name breaks this file rather than drifting
// from it. Only the relations are widened, via `Pick` — a form row holds a
// plain `{ id, name }`, and the model classes carry private fields that make
// them unassignable from one. Building real instances instead would mean
// inventing the fields the form has no value for (a `SiteBasic` needs lat/lon,
// a `MaterialRate` needs a whole `CompanyBasic`) purely to satisfy a
// constructor, so a reader could no longer tell real data from filler.
type NamedRecord = Pick<ItemNameAndId, 'id' | 'name'>;

// Both unit fields carry the OrderUnitOfMeasure value, never its display
// label: `unitOfMeasureOptions` renders POUND as "Lbs" and CUBIC_METER as
// "Cubic Meter", and a label matches none of the unit comparisons below, so
// the material price would silently stop folding into the delivered rate.
export type QuoteMaterialTotal = Pick<ProjectMaterialTotal, 'id' | 'totalQuantity'> & {
  material: Nullable<NamedRecord>;
  unitOfMeasure: Nullable<Pick<ItemNameAndId, 'id'>>;
};

export type QuoteMaterialRate = Pick<MaterialRate, 'id' | 'sellPrice'> & {
  material: Nullable<NamedRecord>;
  site: Nullable<NamedRecord>;
  // A saved rate always carries a unit; a form row the user has not finished
  // filling in does not, so this one field is looser than the model's.
  unitOfMeasure: Nullable<MaterialRate['unitOfMeasure']>;
};

export type QuoteRateCard = Pick<
  SiteRateSchedule,
  'id' | 'name' | 'customerRateValue' | 'customerRateType' | 'quantity'
> & {
  pickupSite: Nullable<NamedRecord>;
  dropoffSite: Nullable<NamedRecord>;
  material: Nullable<NamedRecord>;
  equipmentType: Nullable<Pick<ItemNameAndId, 'name'>>;
};

export type QuoteLineItemsProject = Pick<
  Project,
  'materialRateItemized' | 'taxFreight' | 'taxMaterial' | 'taxAddOns' | 'taxRate'
> & {
  projectMaterialTotals: QuoteMaterialTotal[];
};

interface QuoteLineItemsInput {
  project: QuoteLineItemsProject;
  currencyFormatter: (value: number | string) => string;
  materialRates: QuoteMaterialRate[];
  siteRateSchedules: QuoteRateCard[];
  addOnAssignments: AddOnAssignment[];
}

export function buildQuoteLineItems({
  project,
  currencyFormatter,
  materialRates,
  siteRateSchedules,
  addOnAssignments,
}: QuoteLineItemsInput) {
  const materialTotals = project.projectMaterialTotals ?? [];
  const qtyByMaterialId = new Map<string, { quantity: string; unit: string }>();
  for (const mt of materialTotals) {
    if (!mt.material) continue;
    const unit = mt.unitOfMeasure?.id ?? '';
    const existing = qtyByMaterialId.get(mt.material.id);
    if (!existing) {
      qtyByMaterialId.set(mt.material.id, { quantity: mt.totalQuantity, unit });
    } else if (existing.unit === unit) {
      // The same material can appear in multiple project material totals (e.g.
      // one per phase). Sum quantities that share a unit so the line total
      // reflects the full project quantity, not just the last row. Differing
      // units are ambiguous to sum, so the first entry is kept.
      qtyByMaterialId.set(mt.material.id, {
        quantity: toDecimal(existing.quantity)
          .plus(toDecimal(mt.totalQuantity))
          .toString(),
        unit,
      });
    }
  }

  // Combined (non-itemized) display: when the project does not itemize material
  // rates, a rate-card row shows one delivered rate — the haul rate plus the
  // sell price of the card's material — mirroring how non-itemized invoices
  // roll freight and material into a single line. Resolved before the materials
  // table so a material whose price rolled into a delivered rate does not ALSO
  // contribute a line total to the material subtotal (double-counted dollars);
  // its per-unit rate stays visible, price-sheet style.
  const shouldCombineMaterialAndHaul = !project.materialRateItemized;
  const pricedMaterialRatesByMaterialId = new Map<string, QuoteMaterialRate[]>();
  for (const mr of materialRates) {
    if (!mr.material || mr.sellPrice === null || mr.sellPrice === undefined) continue;
    const list = pricedMaterialRatesByMaterialId.get(mr.material.id) ?? [];
    list.push(mr);
    pricedMaterialRatesByMaterialId.set(mr.material.id, list);
  }
  // Quantity-based haul rate types and the material unit each is denominated
  // in. Job-based types (per hour/day, flat, commission) have no material-unit
  // basis, so they never combine — mirroring the invoice roll-up guards.
  const unitWordByQuantityRateType: Partial<Record<RateType, string>> = {
    [RateType.RATE_PER_BUSHEL]: 'bushel',
    [RateType.RATE_PER_LOAD]: 'load',
    [RateType.RATE_PER_POUND]: 'pound',
    [RateType.RATE_PER_TON]: 'ton',
    [RateType.RATE_PER_TONNE]: 'tonne',
    [RateType.RATE_PER_YARD]: 'yard',
  };
  // Tolerate plural unit labels ("Tons" vs "ton") when matching.
  const normalizeUnitWord = (unit: string) => unit.trim().toLowerCase().replace(/s$/, '');
  // Display words for a printed freight quantity. Hour and day rates carry no
  // material-unit basis so they never combine, but they still quote a quantity.
  const quantityUnitLabelByRateType: Record<QuantityOrTimeRateType, string> = {
    [RateType.RATE_PER_BUSHEL]: t('project.quote_pdf.unit_bushels'),
    [RateType.RATE_PER_DAY]: t('project.quote_pdf.unit_days'),
    [RateType.RATE_PER_EACH]: t('project.quote_pdf.unit_each'),
    [RateType.RATE_PER_HOUR]: t('project.quote_pdf.unit_hours'),
    [RateType.RATE_PER_LOAD]: t('project.quote_pdf.unit_loads'),
    [RateType.RATE_PER_POUND]: t('project.quote_pdf.unit_pounds'),
    [RateType.RATE_PER_TON]: t('project.quote_pdf.unit_tons'),
    [RateType.RATE_PER_TONNE]: t('project.quote_pdf.unit_tonnes'),
    [RateType.RATE_PER_YARD]: t('project.quote_pdf.unit_yards'),
  };
  // Flat, commission and flat-commission rates price the whole job, so a
  // quantity has nothing to multiply — the card can still hold a stale one
  // from before the user switched the rate type.
  const rateTypeExtendsByQuantity = (
    rateType: Nullable<RateType>,
  ): rateType is QuantityOrTimeRateType =>
    rateType !== null &&
    rateType !== undefined &&
    (QUANTITY_AND_TIME_RATE_TYPES as readonly RateType[]).includes(rateType);
  const formatFreightQuantity = (quantity: number, rateType: Nullable<RateType>) => {
    const formatted = formatQuantity(quantity);
    return rateTypeExtendsByQuantity(rateType)
      ? `${formatted} ${quantityUnitLabelByRateType[rateType]}`
      : formatted;
  };
  interface CombinedDeliveredRate {
    scheduleId: string;
    // A card that folds several material rates renders one row per rate, so the
    // card id alone is not a unique React key.
    rowKey: string;
    // Null for a haul-only rate, which prints without the combined marker.
    rateDisplay: string | null;
    unitWord: string;
    material: NamedRecord;
    materialSiteName: string;
    // Halves of the delivered rate, so a taxable base can count haul and
    // material dollars against their own tax flags instead of the folded total.
    haulRatePerUnit: string;
    materialRatePerUnit: string;
  }
  const combinedDeliveredRates: CombinedDeliveredRate[] = [];
  const combinedRatesForCard = (scheduleId: string) =>
    combinedDeliveredRates.filter((entry) => entry.scheduleId === scheduleId);
  const formatCombinedRate = (rateValue: number, rateType: Nullable<RateType>) => {
    const rateTypeLabel = rateType ? getRateTypeLabel(rateType) : '';
    return `${currencyFormatter(rateValue)}${rateTypeLabel ? ` ${rateTypeLabel}` : ''}`;
  };
  const materialIdsCombinedIntoFreight = new Set<string>();
  const materialRateIdsClaimedByOwnCard = new Set<string>();
  const materialRateIdsFoldedByWildcardCard = new Set<string>();
  if (shouldCombineMaterialAndHaul) {
    for (const schedule of siteRateSchedules) {
      const rateValue = schedule.customerRateValue;
      const hasRate = rateValue !== null && rateValue !== undefined;
      if (!hasRate || !schedule.material) continue;
      const haulUnitWord = schedule.customerRateType
        ? unitWordByQuantityRateType[schedule.customerRateType]
        : undefined;
      const pricedRates = pricedMaterialRatesByMaterialId.get(schedule.material.id) ?? [];
      const ratesAtPickupSite = pricedRates.filter(
        (mr) => mr.site?.id && mr.site.id === schedule.pickupSite?.id,
      );
      // Prefer the material rate priced at the card's pickup site; a material
      // priced at exactly one site is also unambiguous. Anything else (no
      // priced rate, or several candidate sites) keeps the haul-only rate —
      // values are shown when present, never guessed.
      let resolvedMaterialRate: QuoteMaterialRate | null = null;
      if (ratesAtPickupSite.length === 1) {
        resolvedMaterialRate = ratesAtPickupSite[0];
      } else if (pricedRates.length === 1) {
        resolvedMaterialRate = pricedRates[0];
      }
      const materialUnit = resolvedMaterialRate?.unitOfMeasure ?? '';
      const materialUnitMatchesHaulUnit =
        Boolean(haulUnitWord) && normalizeUnitWord(materialUnit) === haulUnitWord;
      const materialHasNoPrice = pricedRates.length === 0;
      if (materialHasNoPrice && haulUnitWord) {
        // With no material price to add, the haul rate already is the whole
        // delivered price, so extending it hides no material dollars.
        combinedDeliveredRates.push({
          scheduleId: schedule.id,
          rowKey: schedule.id,
          rateDisplay: null,
          unitWord: haulUnitWord,
          material: schedule.material,
          materialSiteName: '',
          haulRatePerUnit: toDecimal(rateValue).toString(),
          materialRatePerUnit: '0',
        });
      } else if (resolvedMaterialRate && materialUnitMatchesHaulUnit) {
        const combinedValue = toDecimal(rateValue).plus(
          toDecimal(resolvedMaterialRate.sellPrice),
        );
        combinedDeliveredRates.push({
          scheduleId: schedule.id,
          rowKey: schedule.id,
          rateDisplay: formatCombinedRate(
            combinedValue.toNumber(),
            schedule.customerRateType,
          ),
          unitWord: haulUnitWord ?? '',
          material: schedule.material,
          materialSiteName: resolvedMaterialRate.site?.name ?? '',
          haulRatePerUnit: toDecimal(rateValue).toString(),
          materialRatePerUnit: toDecimal(resolvedMaterialRate.sellPrice).toString(),
        });
        materialIdsCombinedIntoFreight.add(schedule.material.id);
        materialRateIdsClaimedByOwnCard.add(resolvedMaterialRate.id);
      }
    }
    // A card naming no material quotes the whole pickup site, so it folds into
    // every material priced there that the loop above did not already claim for
    // a card of its own material. Two such cards at one site quote alternative
    // prices, so this loop never claims a rate away from its siblings.
    for (const schedule of siteRateSchedules) {
      const rateValue = schedule.customerRateValue;
      const hasRate = rateValue !== null && rateValue !== undefined;
      if (!hasRate || schedule.material) continue;
      const haulUnitWord = schedule.customerRateType
        ? unitWordByQuantityRateType[schedule.customerRateType]
        : undefined;
      const pickupSiteId = schedule.pickupSite?.id;
      if (!haulUnitWord || !pickupSiteId) continue;
      for (const materialRate of materialRates) {
        if (!materialRate.material) continue;
        if (materialRate.sellPrice === null || materialRate.sellPrice === undefined) {
          continue;
        }
        if (materialRate.site?.id !== pickupSiteId) continue;
        if (materialRateIdsClaimedByOwnCard.has(materialRate.id)) continue;
        if (normalizeUnitWord(materialRate.unitOfMeasure ?? '') !== haulUnitWord)
          continue;
        const combinedValue = toDecimal(rateValue).plus(
          toDecimal(materialRate.sellPrice),
        );
        combinedDeliveredRates.push({
          scheduleId: schedule.id,
          rowKey: `${schedule.id}:${materialRate.id}`,
          rateDisplay: formatCombinedRate(
            combinedValue.toNumber(),
            schedule.customerRateType,
          ),
          unitWord: haulUnitWord,
          material: materialRate.material,
          materialSiteName: materialRate.site?.name ?? '',
          haulRatePerUnit: toDecimal(rateValue).toString(),
          materialRatePerUnit: toDecimal(materialRate.sellPrice).toString(),
        });
        materialIdsCombinedIntoFreight.add(materialRate.material.id);
        materialRateIdsFoldedByWildcardCard.add(materialRate.id);
      }
    }
  }
  const combinedAnyFreightRate = combinedDeliveredRates.some(
    (entry) => entry.rateDisplay !== null,
  );

  interface MaterialRow {
    id: string;
    materialId: string;
    name: string;
    site: string;
    quantity: string;
    unit: string;
    rate: string;
    lineTotal: string;
    lineAmount: Nullable<string>;
    fromMaterialTotal: boolean;
  }

  const renderedMaterialIds = new Set<string>();
  const materialIdsWithLineTotal = new Set<string>();
  const materialRows: MaterialRow[] = [];
  let materialSubtotal = toDecimal(0);
  let hasAnyMaterialLineTotal = false;

  // Primary source: material rates (has site + sell price)
  for (const mr of materialRates) {
    if (!mr.material) continue;
    renderedMaterialIds.add(mr.material.id);
    const qtyInfo = qtyByMaterialId.get(mr.material.id);
    const materialQuantity = qtyInfo?.quantity ?? '';
    const sellPrice = mr.sellPrice;
    const hasSellPrice = sellPrice !== null && sellPrice !== undefined;
    const unit = mr.unitOfMeasure ?? '';
    const rateStr = hasSellPrice
      ? `${currencyFormatter(sellPrice)}/${unit.toLowerCase()}`
      : '';
    // The project quantity is per-material, not per-site, so a material priced
    // at several sites must be counted once. Attach the quantity and line total
    // to the first *priced* rate row for the material — keying on the first
    // priced row (not merely the first row) both avoids double-counting and
    // avoids losing the total when an earlier rate row has no sell price.
    const hasQuantity = materialQuantity.trim().length > 0;
    // The quantity comes from the project material total while the rate carries
    // its own unit — multiplying across mismatched units (e.g. a Load quantity
    // against a per-Ton rate) would print a wrong dollar figure, so those rows
    // degrade to rate-only.
    const quantityUnitMatchesRateUnit =
      qtyInfo !== undefined &&
      qtyInfo.unit.trim().toLowerCase() === unit.trim().toLowerCase();
    // A material whose price rolled into a combined freight rate keeps its
    // per-unit rate on display but contributes no line total — the dollars
    // already live in the delivered rate.
    const priceRolledIntoFreight = materialIdsCombinedIntoFreight.has(mr.material.id);
    const shouldCountLineTotal =
      hasSellPrice &&
      hasQuantity &&
      quantityUnitMatchesRateUnit &&
      !priceRolledIntoFreight &&
      !materialIdsWithLineTotal.has(mr.material.id);
    let quantity = '';
    let lineTotal = '';
    let lineAmount: Nullable<string> = null;
    if (shouldCountLineTotal) {
      materialIdsWithLineTotal.add(mr.material.id);
      quantity = materialQuantity;
      const amount = toDecimal(materialQuantity).times(toDecimal(sellPrice));
      materialSubtotal = materialSubtotal.plus(amount);
      hasAnyMaterialLineTotal = true;
      lineAmount = amount.toString();
      lineTotal = currencyFormatter(amount.toNumber());
    }
    materialRows.push({
      id: mr.id,
      materialId: mr.material.id,
      name: mr.material.name,
      site: mr.site?.name ?? '',
      quantity,
      unit,
      rate: rateStr,
      lineTotal,
      lineAmount,
      fromMaterialTotal: false,
    });
  }

  // Secondary: remaining materials from project_material_totals not in rates
  for (const mt of materialTotals) {
    if (!mt.material || renderedMaterialIds.has(mt.material.id)) continue;
    materialRows.push({
      id: mt.id,
      materialId: mt.material.id,
      name: mt.material.name,
      site: '',
      quantity: mt.totalQuantity,
      unit: mt.unitOfMeasure?.id ?? '',
      rate: '',
      lineTotal: '',
      lineAmount: null,
      fromMaterialTotal: true,
    });
  }

  const hasMaterials = materialRows.length > 0;

  const formatHaulOnlyRate = (schedule: QuoteRateCard) => {
    const rateValue = schedule.customerRateValue;
    if (rateValue === null || rateValue === undefined) return '';
    const rateTypeLabel = schedule.customerRateType
      ? getRateTypeLabel(schedule.customerRateType)
      : '';
    return `${currencyFormatter(rateValue)}${rateTypeLabel ? ` ${rateTypeLabel}` : ''}`;
  };

  // Freight — one row per rate card (truck type). A card that carries an
  // estimated quantity is extended to a line total; one without stays a rate.
  let freightSubtotal = toDecimal(0);
  let hasAnyFreightLineTotal = false;
  const freightRows = siteRateSchedules.map((schedule) => {
    // The itemized freight table has one row per card, so it can only print a
    // delivered rate when the card folded exactly one material.
    const foldedRates = combinedRatesForCard(schedule.id);
    const soleCombinedRate = foldedRates.length === 1 ? foldedRates[0].rateDisplay : null;
    const rate = soleCombinedRate ?? formatHaulOnlyRate(schedule);
    const pickupName = schedule.pickupSite?.name ?? '';
    const dropoffName = schedule.dropoffSite?.name ?? '';
    // Use the word "to" rather than an arrow: the PDF renders in the built-in
    // Helvetica (WinAnsi) font, which has no "→" (U+2192) glyph, so an arrow
    // would silently drop and the two site names would run together.
    const route =
      pickupName && dropoffName
        ? `${pickupName} to ${dropoffName}`
        : pickupName || dropoffName;
    const quantity = schedule.quantity;
    const rateValue = schedule.customerRateValue;
    const printsQuantity =
      quantity !== null &&
      quantity !== undefined &&
      rateTypeExtendsByQuantity(schedule.customerRateType);
    // A user without `view_rate` receives a nil rate, so a card can carry a
    // quantity with nothing to multiply it by.
    const canExtendToLineTotal =
      printsQuantity && rateValue !== null && rateValue !== undefined;
    let lineTotal = '';
    if (canExtendToLineTotal) {
      const amount = toDecimal(quantity).times(toDecimal(rateValue));
      freightSubtotal = freightSubtotal.plus(amount);
      hasAnyFreightLineTotal = true;
      lineTotal = currencyFormatter(amount.toNumber());
    }
    return {
      id: schedule.id,
      equipType: schedule.equipmentType?.name ?? '',
      description: schedule.name ?? '',
      route,
      quantity: printsQuantity
        ? formatFreightQuantity(quantity, schedule.customerRateType)
        : '',
      rate,
      lineTotal,
      rateIsCombined: soleCombinedRate !== null,
    };
  });
  const hasFreight = freightRows.length > 0;

  interface DeliveredPriceRow {
    id: string;
    materialId: string;
    equipmentType: string;
    material: string;
    source: string;
    quantity: string;
    rate: string;
    salesPrice: string;
    salesPriceAmount: Nullable<string>;
    haulAmount: Nullable<string>;
    materialAmount: Nullable<string>;
    rateIsCombined: boolean;
  }

  const formatQuantityWithUnit = (quantity: string, unit: string) => {
    if (quantity.trim().length === 0) return '';
    const formatted = formatQuantity(quantity);
    return unit ? `${formatted} ${unit}` : formatted;
  };

  // A card that folded the materials of a whole pickup site quotes each of them
  // at that material's own quantity, so its single estimated quantity no longer
  // describes any one of those rows.
  const cardFoldedSiteMaterials = (schedule: QuoteRateCard) =>
    !schedule.material && combinedRatesForCard(schedule.id).length > 0;
  // The card's own estimated quantity is denominated in the card's rate type, so
  // it needs no unit reconciliation and takes precedence over the project
  // material total. It may only be extended when the card's rate is the whole
  // delivered price: either the material price is folded in (or the material has
  // none), or the card names no material and folded none.
  const extendsByOwnQuantity = (schedule: QuoteRateCard) =>
    schedule.quantity !== null &&
    schedule.quantity !== undefined &&
    schedule.customerRateValue !== null &&
    schedule.customerRateValue !== undefined &&
    (combinedRatesForCard(schedule.id).length > 0 || !schedule.material) &&
    !cardFoldedSiteMaterials(schedule) &&
    rateTypeExtendsByQuantity(schedule.customerRateType);
  // A material one card already extends by its own quantity must not be extended
  // a second time by a sibling card falling back to the shared project material
  // total — the same dollars would land on two delivered lines.
  const materialIdsExtendedByCardQuantity = new Set(
    siteRateSchedules.flatMap((schedule) =>
      schedule.material && extendsByOwnQuantity(schedule) ? [schedule.material.id] : [],
    ),
  );

  const deliveredPriceRows: DeliveredPriceRow[] = [];
  // Materials whose quantity a delivered line already carries, so their own
  // row is dropped rather than repeat the tonnage in the same table.
  const materialIdsOnDeliveredLine = new Set<string>();
  if (shouldCombineMaterialAndHaul) {
    const pushDeliveredRow = (
      schedule: QuoteRateCard,
      combinedRate: CombinedDeliveredRate | null,
    ) => {
      const rowMaterial = combinedRate?.material ?? schedule.material;
      const quantityInfo = rowMaterial ? qtyByMaterialId.get(rowMaterial.id) : undefined;
      const cardQuantity = schedule.quantity;
      const cardRateValue = schedule.customerRateValue;
      const extendsByCardQuantity =
        cardQuantity !== null &&
        cardQuantity !== undefined &&
        extendsByOwnQuantity(schedule);
      // Only a resolved delivered rate may be extended. A haul-only rate
      // excludes the material price, so multiplying it would print a delivered
      // amount that is silently missing the material dollars.
      const canExtendToSalesPrice =
        combinedRate !== null &&
        quantityInfo !== undefined &&
        quantityInfo.quantity.trim().length > 0 &&
        normalizeUnitWord(quantityInfo.unit) === combinedRate.unitWord &&
        !(rowMaterial && materialIdsExtendedByCardQuantity.has(rowMaterial.id));
      let salesPriceAmount: Nullable<string> = null;
      let haulAmount: Nullable<string> = null;
      let materialAmount: Nullable<string> = null;
      const extendedQuantity = extendsByCardQuantity
        ? cardQuantity
        : canExtendToSalesPrice && quantityInfo
          ? quantityInfo.quantity
          : null;
      if (extendedQuantity !== null && (combinedRate || extendsByCardQuantity)) {
        const quantity = toDecimal(extendedQuantity);
        // A card with nothing folded in has no entry, so its own rate is the
        // whole haul price.
        const haulRate = toDecimal(combinedRate?.haulRatePerUnit ?? cardRateValue);
        const materialRate = toDecimal(combinedRate?.materialRatePerUnit ?? 0);
        haulAmount = quantity.times(haulRate).toString();
        materialAmount = quantity.times(materialRate).toString();
        salesPriceAmount = quantity.times(haulRate.plus(materialRate)).toString();
      }
      if (salesPriceAmount !== null && rowMaterial) {
        materialIdsOnDeliveredLine.add(rowMaterial.id);
      }
      // The project quantity belongs to the material, not the rate card, so it
      // is shown only when it feeds this row's delivered total — otherwise the
      // material's own row carries it and the same tonnage would appear twice
      // in one table. A card's own estimated quantity shows even when nothing
      // can extend it to a sales price.
      const printsCardQuantity =
        cardQuantity !== null &&
        cardQuantity !== undefined &&
        !cardFoldedSiteMaterials(schedule) &&
        rateTypeExtendsByQuantity(schedule.customerRateType);
      let deliveredQuantity = '';
      if (printsCardQuantity) {
        deliveredQuantity = formatFreightQuantity(
          cardQuantity,
          schedule.customerRateType,
        );
      } else if (canExtendToSalesPrice && quantityInfo) {
        deliveredQuantity = formatQuantityWithUnit(
          quantityInfo.quantity,
          quantityInfo.unit,
        );
      }
      deliveredPriceRows.push({
        id: combinedRate?.rowKey ?? schedule.id,
        materialId: rowMaterial?.id ?? '',
        equipmentType: schedule.equipmentType?.name ?? '',
        material: rowMaterial?.name ?? '',
        // A rate card need not name a pickup site. The source the customer
        // cares about is the pit the material is priced at, so fall back to
        // the site of the material rate folded into the delivered rate.
        source: schedule.pickupSite?.name || (combinedRate?.materialSiteName ?? ''),
        quantity: deliveredQuantity,
        rate: combinedRate?.rateDisplay ?? formatHaulOnlyRate(schedule),
        salesPrice:
          salesPriceAmount === null
            ? ''
            : currencyFormatter(toDecimal(salesPriceAmount).toNumber()),
        salesPriceAmount,
        haulAmount,
        materialAmount,
        rateIsCombined: Boolean(combinedRate?.rateDisplay),
      });
    };
    for (const schedule of siteRateSchedules) {
      const foldedRates = combinedRatesForCard(schedule.id);
      if (foldedRates.length === 0) {
        pushDeliveredRow(schedule, null);
        continue;
      }
      for (const combinedRate of foldedRates) {
        pushDeliveredRow(schedule, combinedRate);
      }
    }
    // Coverage is tracked per material rate, not per material: a material
    // priced at several sites keeps the rows for the sites no rate card
    // consumed, so those prices stay on the quote.
    for (const row of materialRows) {
      if (
        materialRateIdsClaimedByOwnCard.has(row.id) ||
        materialRateIdsFoldedByWildcardCard.has(row.id)
      ) {
        continue;
      }
      // Only the unpriced project-material-total row duplicates a delivered
      // line. A material rate priced at a site no card consumed still carries
      // its own price and must stay on the quote.
      if (row.fromMaterialTotal && materialIdsOnDeliveredLine.has(row.materialId)) {
        continue;
      }
      deliveredPriceRows.push({
        id: row.id,
        materialId: row.materialId,
        equipmentType: '',
        material: row.name,
        source: row.site,
        quantity: formatQuantityWithUnit(row.quantity, row.unit),
        rate: row.rate,
        salesPrice: row.lineTotal,
        salesPriceAmount: row.lineAmount,
        haulAmount: null,
        materialAmount: row.lineAmount,
        rateIsCombined: false,
      });
    }
  }
  const hasDeliveredPricing = deliveredPriceRows.length > 0;
  // Two rows for one material quoted at two equipment types are alternative
  // prices carrying the same quantity, so totalling them double-counts the
  // tonnage — the subtotal is withheld rather than printed wrong.
  const pricedDeliveredRows = deliveredPriceRows.filter(
    (row) => row.salesPriceAmount !== null,
  );
  const pricedMaterialIds = new Set(pricedDeliveredRows.map((row) => row.materialId));
  const everyMaterialPricedOnce = pricedMaterialIds.size === pricedDeliveredRows.length;
  const everyMaterialTotalPriced = materialTotals.every(
    (mt) => mt.material && pricedMaterialIds.has(mt.material.id),
  );
  const canSubtotalDelivered =
    materialTotals.length > 1 && everyMaterialPricedOnce && everyMaterialTotalPriced;
  const deliveredSubtotal = pricedDeliveredRows.reduce(
    (sum, row) => sum.plus(toDecimal(row.salesPriceAmount ?? 0)),
    toDecimal(0),
  );
  const deliveredHaulSubtotal = pricedDeliveredRows.reduce(
    (sum, row) => sum.plus(toDecimal(row.haulAmount ?? 0)),
    toDecimal(0),
  );
  const deliveredMaterialSubtotal = pricedDeliveredRows.reduce(
    (sum, row) => sum.plus(toDecimal(row.materialAmount ?? 0)),
    toDecimal(0),
  );
  const deliveredPricingIncludesFreight =
    shouldCombineMaterialAndHaul && siteRateSchedules.length > 0;

  // Add-ons — one detailed row each. Flat fees (rate × quantity) roll into the
  // add-on subtotal; percent-of-total and fuel-surcharge add-ons have no fixed
  // amount at quote time and show "est." instead.
  let addOnSubtotal = toDecimal(0);
  let hasAnyAddOnAmount = false;
  const addOnRows = addOnAssignments.map((addOn) => {
    const quantityOrPercent = addOn.isPercentOfTotal
      ? formatPercentage(addOn.percentage)
      : String(addOn.quantity);
    let basis: string;
    if (addOn.isPercentOfTotal) {
      basis = t('project.quote_pdf.of_total');
    } else if (addOn.isFuelSurcharge) {
      basis = t('project.quote_pdf.fuel_schedule');
    } else {
      const rateType = addOn.revenueCostRateType;
      basis = rateType
        ? (rateTypeTextByAddOnRateType[rateType] ?? t('project.quote_pdf.flat'))
        : t('project.quote_pdf.flat');
    }
    const flatAmount = computeAddOnFlatAmount(addOn);
    let amount: string;
    if (flatAmount !== null) {
      addOnSubtotal = addOnSubtotal.plus(toDecimal(flatAmount));
      hasAnyAddOnAmount = true;
      amount = currencyFormatter(flatAmount);
    } else {
      amount = t('project.quote_pdf.estimated');
    }
    return { id: addOn.id, name: addOn.name, quantityOrPercent, basis, amount };
  });

  // Tax — computed on the taxable subtotals we can total. The combined view
  // folds haul and material into one delivered rate, so it taxes the split
  // halves of each delivered line rather than the folded total; every dollar
  // is counted once, against its own flag. Alternative rows for one material
  // cannot be totalled, so they carry no tax either.
  const canTaxDelivered = shouldCombineMaterialAndHaul && everyMaterialPricedOnce;
  const taxableFreightSubtotal = shouldCombineMaterialAndHaul
    ? canTaxDelivered
      ? deliveredHaulSubtotal
      : toDecimal(0)
    : freightSubtotal;
  const taxableMaterialSubtotal = shouldCombineMaterialAndHaul
    ? canTaxDelivered
      ? deliveredMaterialSubtotal
      : toDecimal(0)
    : materialSubtotal;
  const freightIsTaxable =
    Boolean(project.taxFreight) && taxableFreightSubtotal.greaterThan(0);
  const taxableBase = (project.taxMaterial ? taxableMaterialSubtotal : toDecimal(0))
    .plus(project.taxAddOns ? addOnSubtotal : toDecimal(0))
    .plus(project.taxFreight ? taxableFreightSubtotal : toDecimal(0));
  const taxAmount = toDecimal(project.taxRate).div(100).times(taxableBase);
  const taxableBasisItems = [
    project.taxMaterial ? t('project.form.taxable_material') : '',
    project.taxAddOns ? t('project.form.taxable_add_ons') : '',
    freightIsTaxable ? t('project.form.taxable_freight') : '',
  ].filter(Boolean);
  const taxBasisSuffix =
    taxableBasisItems.length > 0 ? ` (${taxableBasisItems.join(', ')})` : '';
  const hasTaxRate =
    project.taxRate !== null &&
    project.taxRate !== undefined &&
    Number(project.taxRate) > 0;
  // Only show the tax line when it computes to a positive amount. Gating on the
  // taxable base itself (not just the taxMaterial/taxAddOns flags) also covers
  // the case where add-ons are taxable but all of them are percent-of-total or
  // fuel surcharges with no fixed amount — the base is still 0, so a "$0.00"
  // tax line would be misleading.
  const hasComputableTax = hasTaxRate && taxableBase.greaterThan(0);

  return {
    addOnRows,
    addOnSubtotal,
    canSubtotalDelivered,
    combinedAnyFreightRate,
    deliveredPriceRows,
    deliveredPricingIncludesFreight,
    deliveredSubtotal,
    freightRows,
    freightSubtotal,
    hasAnyAddOnAmount,
    hasAnyFreightLineTotal,
    hasAnyMaterialLineTotal,
    hasComputableTax,
    hasDeliveredPricing,
    hasFreight,
    hasMaterials,
    materialRows,
    materialSubtotal,
    shouldCombineMaterialAndHaul,
    taxAmount,
    taxBasisSuffix,
  };
}
